if (!customElements.get('pr-collection')) {
  const STORAGE_KEY = 'pr-collection-cols';
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  const readStoredCols = () => {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      return ['2', '3', '4'].includes(value) ? value : null;
    } catch (e) {
      return null;
    }
  };

  const storeCols = (cols) => {
    try {
      localStorage.setItem(STORAGE_KEY, cols);
    } catch (e) {
      /* storage unavailable: the preference just won't persist */
    }
  };

  class PRCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.parser = new DOMParser();
      this.priceTimer = null;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('popstate', this.onPopState);

      this.setCols(readStoredCols() || this.dataset.cols, false);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('popstate', this.onPopState);
      document.documentElement.classList.remove('prc-lock');
      clearTimeout(this.priceTimer);
      if (this.controller) this.controller.abort();
    }

    get drawer() {
      return this.querySelector('[data-prc-drawer]');
    }

    get sortToggle() {
      return this.querySelector('[data-prc-sort-toggle]');
    }

    get sortList() {
      return this.querySelector('[data-prc-sort-list]');
    }

    /* ---------- Events ---------- */

    onClick(event) {
      const target = event.target;

      const swatch = target.closest('[data-prc-swatch]');
      if (swatch) {
        event.preventDefault();
        this.selectSwatch(swatch);
        return;
      }

      const density = target.closest('[data-prc-density]');
      if (density) {
        this.setCols(density.dataset.prcDensity, true);
        return;
      }

      const openFilters = target.closest('[data-prc-open-filters]');
      if (openFilters) {
        this.openDrawer(openFilters);
        return;
      }

      if (target.closest('[data-prc-close-filters]')) {
        this.closeDrawer();
        return;
      }

      if (target.closest('[data-prc-sort-toggle]')) {
        this.toggleSort();
        return;
      }

      const loadMore = target.closest('[data-prc-load-more]');
      if (loadMore) {
        event.preventDefault();
        this.load(loadMore.href, { append: true });
        return;
      }

      const link = target.closest('[data-prc-link]');
      if (link && !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button)) {
        event.preventDefault();
        this.closeSort(false);
        this.load(link.href, { scroll: link.hasAttribute('data-prc-scroll') });
      }
    }

    onDocumentClick(event) {
      if (!this.contains(event.target) || !event.target.closest('[data-prc-sort]')) {
        this.closeSort(false);
      }
    }

    onChange(event) {
      const form = event.target.closest('[data-prc-filter-form]');
      if (!form) return;
      if (event.target.type === 'number') {
        clearTimeout(this.priceTimer);
        this.priceTimer = setTimeout(() => this.submitFilters(form), 600);
      } else {
        this.submitFilters(form);
      }
    }

    onInput(event) {
      if (event.target.type === 'number') clearTimeout(this.priceTimer);
    }

    onSubmit(event) {
      const form = event.target.closest('[data-prc-filter-form]');
      if (!form) return;
      event.preventDefault();
      clearTimeout(this.priceTimer);
      this.submitFilters(form);
    }

    onKeydown(event) {
      const drawer = this.drawer;
      const drawerOpen = drawer && drawer.classList.contains('is-open');

      if (event.key === 'Escape') {
        if (drawerOpen) {
          event.preventDefault();
          this.closeDrawer();
        } else if (this.sortList && !this.sortList.hidden) {
          event.preventDefault();
          this.closeSort(true);
        }
        return;
      }

      if (event.key === 'Tab' && drawerOpen) {
        this.trapFocus(event, drawer);
        return;
      }

      if (event.target.closest('[data-prc-sort]')) this.onSortKeydown(event);
    }

    onPopState() {
      this.load(window.location.href, { push: false });
    }

    /* ---------- Density ---------- */

    setCols(cols, persist) {
      if (!['2', '3', '4'].includes(String(cols))) cols = this.dataset.defaultCols;
      cols = String(cols);
      this.dataset.cols = cols;
      this.querySelectorAll('[data-prc-density]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.prcDensity === cols));
      });
      this.updateSizes();
      if (persist) storeCols(cols);
    }

    updateSizes(root = this) {
      const vw = Math.round(100 / Number(this.dataset.cols));
      root.querySelectorAll('.prc-card__img').forEach((img) => {
        img.sizes = `(min-width: 1024px) ${vw}vw, 50vw`;
      });
    }

    /* ---------- Swatches ---------- */

    selectSwatch(swatch) {
      const card = swatch.closest('[data-prc-card]');
      const img = card.querySelector('.prc-card__img--primary');
      const link = card.querySelector('[data-prc-card-link]');

      card.querySelectorAll('[data-prc-swatch]').forEach((other) => {
        other.setAttribute('aria-pressed', String(other === swatch));
      });

      if (img && swatch.dataset.src) {
        img.srcset = swatch.dataset.srcset;
        img.src = swatch.dataset.src;
        img.alt = swatch.dataset.alt || img.alt;
        card.dataset.swatched = '';
      }
      if (link && swatch.dataset.url) link.href = swatch.dataset.url;
    }

    /* ---------- Sort dropdown ---------- */

    toggleSort() {
      if (this.sortList.hidden) this.openSort();
      else this.closeSort(true);
    }

    openSort(focusFirst = false) {
      this.sortList.hidden = false;
      this.sortToggle.setAttribute('aria-expanded', 'true');
      if (focusFirst) this.sortOptions()[0]?.focus();
    }

    closeSort(returnFocus) {
      const list = this.sortList;
      if (!list || list.hidden) return;
      list.hidden = true;
      this.sortToggle.setAttribute('aria-expanded', 'false');
      if (returnFocus) this.sortToggle.focus();
    }

    sortOptions() {
      return Array.from(this.querySelectorAll('.prc-sort__option'));
    }

    onSortKeydown(event) {
      const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      event.preventDefault();

      if (event.target.closest('[data-prc-sort-toggle]')) {
        this.openSort();
        const options = this.sortOptions();
        (event.key === 'ArrowUp' || event.key === 'End' ? options[options.length - 1] : options[0])?.focus();
        return;
      }

      const options = this.sortOptions();
      const index = options.indexOf(document.activeElement);
      let next = index;
      if (event.key === 'ArrowDown') next = (index + 1) % options.length;
      if (event.key === 'ArrowUp') next = (index - 1 + options.length) % options.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = options.length - 1;
      options[next]?.focus();
    }

    /* ---------- Filter drawer ---------- */

    openDrawer(trigger) {
      const drawer = this.drawer;
      this.drawerTrigger = trigger;
      drawer.removeAttribute('inert');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('is-open');
      this.querySelector('.prc-shade').hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('prc-lock');
      requestAnimationFrame(() => drawer.querySelector('.prc-drawer__close').focus());
    }

    closeDrawer() {
      const drawer = this.drawer;
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      this.querySelector('.prc-shade').hidden = true;
      document.documentElement.classList.remove('prc-lock');
      if (this.drawerTrigger) {
        this.drawerTrigger.setAttribute('aria-expanded', 'false');
        this.drawerTrigger.focus();
      }
    }

    trapFocus(event, container) {
      const items = Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!container.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    submitFilters(form) {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
      const query = params.toString();
      this.load(`${form.getAttribute('action')}${query ? `?${query}` : ''}`);
    }

    /* ---------- Section Rendering API ---------- */

    async load(url, { append = false, push = true, scroll = false } = {}) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const target = new URL(url, window.location.origin);
      const request = new URL(target);
      request.searchParams.set('section_id', this.sectionId);

      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(request, { signal: this.controller.signal });
        if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
        const doc = this.parser.parseFromString(await response.text(), 'text/html');
        const incoming = doc.querySelector('pr-collection');
        if (!incoming) throw new Error('Section markup not found');

        if (append) this.appendProducts(incoming);
        else this.replaceRegions(incoming);

        if (push && !append) window.history.pushState({}, '', target);
        if (scroll) this.querySelector('.prc-toolbar').scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = target.toString();
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    replaceRegions(incoming) {
      const openGroups = Array.from(this.querySelectorAll('.prc-group[open]')).map((el) => el.dataset.group);
      const activeId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : '';

      ['[data-prc-results]', '[data-prc-filters]', '[data-prc-sort-list]'].forEach((selector) => {
        const current = this.querySelector(selector);
        const next = incoming.querySelector(selector);
        if (current && next) {
          const wasHidden = current.hidden;
          current.innerHTML = next.innerHTML;
          current.hidden = wasHidden;
        }
      });

      this.querySelectorAll('.prc-group').forEach((group) => {
        if (openGroups.includes(group.dataset.group)) group.open = true;
      });
      this.updateSizes();

      if (activeId) {
        const el = document.getElementById(activeId);
        if (el) el.focus({ preventScroll: true });
      }
    }

    appendProducts(incoming) {
      const grid = this.querySelector('[data-prc-grid]');
      const nextGrid = incoming.querySelector('[data-prc-grid]');
      if (grid && nextGrid) {
        const added = Array.from(nextGrid.children);
        grid.append(...added);
        added.forEach((card) => this.updateSizes(card));
        added[0]?.querySelector('a')?.focus({ preventScroll: true });
      }

      const pagination = this.querySelector('[data-prc-pagination]');
      const nextPagination = incoming.querySelector('[data-prc-pagination]');
      if (pagination) {
        if (nextPagination) pagination.innerHTML = nextPagination.innerHTML;
        else pagination.remove();
      }

    }
  }

  customElements.define('pr-collection', PRCollection);
}
